import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Avatar,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import useNotification from '../../../hooks/useNotification';
import { api } from '../../../services/api';
import SectionAvatarSelector from '../../layout/SectionAvatarSelector';

/**
 * Section Avatar Component with selector 
 * Allows selecting an avatar from a grid for a specific section
 * 
 * @param {Object} props - Component props
 * @param {string} props.sectionId - The ID of the section
 * @param {string} props.sectionName - The name of the section
 * @param {string} props.avatarUrl - Current avatar URL for the section (if any)
 * @param {string} props.avatarName - Name of the avatar for this section
 * @param {string} props.projectId - The ID of the project (for avatar uniqueness per project)
 * @param {Function} props.onAvatarChange - Callback when avatar is changed
 * @param {number} props.size - Size of the avatar (default: 80)
 * @returns {React.ReactElement} Section avatar component with selector
 */
const SectionAvatarSelectComponent = ({ 
  sectionId, 
  sectionName, 
  avatarUrl, 
  avatarName, 
  projectId,
  onAvatarChange, 
  size = 80 
}) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(
    avatarUrl ? { photoUrl: avatarUrl, name: avatarName } : null
  );
  const [customAvatarName, setCustomAvatarName] = useState(avatarName || '');
  const [nameError, setNameError] = useState('');

  // Get default avatar name based on section
  const getDefaultAvatarName = (sectionName) => {
    const avatarNames = {
      'Functional analysis': 'Felix',
      'Design': 'Diana',
      'User Stories': 'Uma',
      'User Roles': 'Rachel',
      'Development': 'Devon',
      'Testing': 'Tessa',
      'API': 'Alex',
      'Architecture': 'Archie',
      'Security': 'Sam',
      'Database': 'Dora',
    };
    
    return avatarNames[sectionName] || 'Ailyce';
  };

  // Get display name for avatar
  const displayName = avatarName || getDefaultAvatarName(sectionName);

  // Handle opening avatar selection dialog
  const handleOpenAvatarDialog = () => {
    setAvatarDialogOpen(true);
  };

  // Handle closing avatar selection dialog
  const handleCloseAvatarDialog = () => {
    setAvatarDialogOpen(false);
  };

  // Handle avatar selection
  const handleSelectAvatar = (avatar) => {
    if (!avatar) return;
    
    // Just set the selected avatar but don't close dialog yet
    setSelectedAvatar(avatar);
    // Set default avatar name but allow user to change it
    setCustomAvatarName(avatar.name);
  };
  
  // Handle saving the selected avatar with custom name
  const handleSaveAvatar = async () => {
    // Client-side validation
    if (!customAvatarName.trim()) {
      setNameError('Please enter a name for the avatar');
      return;
    }
    
    // Reset previous errors
    setNameError('');
    
    try {
      setLoading(true);
      
      // Use selected avatar or create default avatar data if none selected
      const avatarPhotoUrl = selectedAvatar ? selectedAvatar.photoUrl : avatarUrl || 'defaultAvatarPath';
      const avatarType = selectedAvatar ? (selectedAvatar.type || 'robot') : 'robot';
      
      // Create data for avatar selection
      const avatarData = {
        sectionId,
        avatarName: customAvatarName.trim(), // Use custom name after trimming whitespace
        photoUrl: avatarPhotoUrl,
        type: avatarType,
        projectId // Include projectId for uniqueness per project
      };
      
      // Send to backend with proper authentication and project context
      const response = await api.post(`/api/sections/${sectionId}/avatar/select?projectId=${projectId}`, avatarData);
      
      // Update UI with new avatar
      if (onAvatarChange && response.data && response.data.avatarUrl) {
        onAvatarChange(response.data.avatarUrl, response.data.avatarName || customAvatarName);
      }
      
      showNotification({
        message: `Avatar for ${sectionName} section updated successfully`,
        type: 'success',
      });
      
      setAvatarDialogOpen(false);
    } catch (error) {
      console.error('Error updating section avatar:', error);
      
      // Check if the error is about duplicate name
      if (error.message && error.message.includes('already in use')) {
        setNameError(error.message);
      } else {
        showNotification({
          message: error.message || `Failed to update avatar for ${sectionName} section`,
          type: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset image error state when avatarUrl changes
  React.useEffect(() => {
    setImageError(false);
  }, [avatarUrl]);

  return (
    <>
      <Box sx={{ 
        position: 'relative', 
        width: size, 
        height: size, 
        mx: 'auto', 
        my: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {/* Avatar Image */}
        <Box sx={{ position: 'relative' }}>
          {avatarUrl && !imageError ? (
            <Box 
              component="img"
              src={avatarUrl}
              alt={displayName}
              onError={() => setImageError(true)}
              sx={{ 
                width: size,
                height: size,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid',
                borderColor: 'rgba(255,255,255,0.2)',
                cursor: 'pointer'
              }}
              onClick={handleOpenAvatarDialog}
            />
          ) : (
            <Avatar
              sx={{
                width: size,
                height: size,
                fontSize: size / 2,
                bgcolor: 'primary.main',
                border: '2px solid',
                borderColor: 'rgba(255,255,255,0.2)',
                cursor: 'pointer'
              }}
              onClick={handleOpenAvatarDialog}
            >
              {displayName.charAt(0)}
            </Avatar>
          )}
          <IconButton
            onClick={handleOpenAvatarDialog}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              width: 32,
              height: 32,
              p: 0.5,
            }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* Avatar Name */}
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1, 
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          {displayName}
        </Typography>
      </Box>

      {/* Avatar Selection Dialog */}
      <Dialog
        open={avatarDialogOpen}
        onClose={handleCloseAvatarDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            backgroundImage: 'none',
            '&::before': { display: 'none' },
            '&::after': { display: 'none' }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          borderBottom: '1px solid', 
          borderColor: 'divider', 
          pb: 2 
        }}>
          <Typography variant="h6" sx={{ fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
            {sectionName} Avatar
          </Typography>
          <IconButton onClick={handleCloseAvatarDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 6 }}>
          {/* Added substantial space at the top */}
          <Box sx={{ mt: 3, mb: 3 }}>
            {/* Layout with regular form styling - Avatar image aligned with the field */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'flex-start',
              gap: 2
            }}>
              {/* Avatar Preview - better aligned with input field */}
              <Box sx={{ mt: 3.5 }}>
                {selectedAvatar ? (
                  <Box 
                    component="img"
                    src={selectedAvatar.photoUrl}
                    alt="Selected avatar"
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.1)',
                      objectFit: 'contain',
                      p: 0.5
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 16,
                      bgcolor: 'primary.main',
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.1)',
                    }}
                  >
                    {(customAvatarName || displayName).charAt(0)}
                  </Avatar>
                )}
              </Box>
              
              {/* Name Input Field with text label */}
              <Box sx={{ flexGrow: 1 }}>
                {/* Text label above input */}
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  Avatar Name
                </Typography>
                
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={customAvatarName}
                  onChange={(e) => {
                    setCustomAvatarName(e.target.value);
                    // Clear error when user starts typing
                    if (nameError) setNameError('');
                  }}
                  placeholder="Enter a name for this avatar"
                  InputProps={{
                    sx: { borderRadius: '8px' }
                  }}
                  // Removed the floating label
                  label={null}
                  error={!!nameError}
                  helperText={nameError || " "} // Empty space as placeholder, or error message
                />
              </Box>
            </Box>
          </Box>
          
          {/* Separator */}
          <Box sx={{ 
            mt: 2, 
            mb: 3, 
            borderTop: '1px solid', 
            borderColor: 'divider' 
          }} />
          
          {/* Avatar Selection Section */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Avatar
          </Typography>
          
          {/* Avatar Grid Selection with pre-selected avatar */}
          <SectionAvatarSelector 
            onSelectAvatar={handleSelectAvatar}
          />
        </DialogContent>
        
        {/* Dialog Actions */}
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={handleCloseAvatarDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAvatar} 
            variant="contained" 
            color="primary"
            disabled={!customAvatarName.trim() || loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SectionAvatarSelectComponent.propTypes = {
  sectionId: PropTypes.string.isRequired,
  sectionName: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  avatarName: PropTypes.string,
  projectId: PropTypes.string,
  onAvatarChange: PropTypes.func,
  size: PropTypes.number
};

export default SectionAvatarSelectComponent;